import { ArrowBackIosNew, ArrowForwardIos } from '@mui/icons-material';
import { IconButton, InputBase, Menu, MenuItem, MobileStepper, Snackbar, Step, StepButton, Stepper, Typography } from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import { Box } from '@mui/system';
import { useEffect, useRef, useState } from 'react';
import SearchIcon from "@mui/icons-material/Search";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import Typed from "typed.js";
import { createSearchParams, useNavigate } from 'react-router-dom';
import validator from "validator";
import CloseIcon from "@mui/icons-material/Close";
import SwipeableViews from 'react-swipeable-views';

const vantagePoints = [
    { name: "netherlands", country: "🇳🇱", url: "https://netherlands-fkenivnaka-ez.a.run.app" },
    { name: "tokyo", country: "🇯🇵", url: "https://tokyo-fkenivnaka-an.a.run.app" },
    { name: "rio de janeiro", country: "🇧🇷", url: "https://saopaulo-fkenivnaka-rj.a.run.app" },
    { name: "london", country: "🇬🇧", url: "https://london-fkenivnaka-nw.a.run.app" },
    { name: "las vegas", country: "🇺🇸", url: "https://vegas-fkenivnaka-wn.a.run.app" },
    { name: "paris", country: "🇫🇷", url: "https://paris-fkenivnaka-od.a.run.app" },
    { name: "hong kong", country: "🇨🇳", url: "https://hongkong-fkenivnaka-df.a.run.app" },
];
const promptStrings = ["spotify.com", "github.com", "discord.com", "medium.com", "princeton.edu", "codepen.io", "fandom.com", "slack.com", "instacart.com", "hulu.com"];

const DemoBox = styled(Box)(
    ({ theme }) => ({
        padding: "6.4rem 16vw 0 16vw",
        height: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        overflowX: "hidden",
        overflowY: "auto",
        [theme.breakpoints.up("sm")]: {
            paddingTop: "7rem",
        },
    })
);

const SearchBox = styled(Box)(
    ({ theme }) => ({
        marginTop: theme.spacing(2.5),
        position: "relative",
        width: "84vw",
        maxWidth: "90rem",
        [theme.breakpoints.up("md")]: {
            width: "68vw",
        },
    })
);

const activeMixin = ({
    backgroundColor: "#fafafa",
    backgroundClip: "padding-box",
    "&:before": {
        content: "''",
        position: "absolute",
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        zIndex: -1,
        margin: -1,
        borderRadius: "inherit",
        background: `linear-gradient(to left, #743ad5, #d53a9d)`,
    },
})

const SearchForm = styled("form", { shouldForwardProp: (prop) => prop !== "focus" })(
    ({ theme, focus }) => ({
        height: "6.4rem",
        width: "100%",
        border: "solid 1px transparent",
        borderRadius: "7rem",
        backgroundColor: alpha("#000", 0.1),
        zIndex: theme.zIndex.appBar,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        overflow: "hidden",
        "&:hover": {
            ...activeMixin,
        },
        ...(focus && activeMixin),
    })
);

const SearchInput = styled(InputBase)(
    ({ theme }) => ({
        width: "100%",
        fontFamily: "'montserrat'",
        fontSize: "2.2rem",
        fontWeight: 500,
        padding: theme.spacing(0, 2.5),
    })
);

const SearchIconWrapper = styled("div")(
    ({ theme }) => ({
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
    })
);

const SearchLocation = styled("div")(
    ({ theme }) => ({
        color: "inherit",
        whiteSpace: "nowrap",
        fontSize: "2.8rem",
        fontWeight: 600,
        overflow: "hidden",
        transition: "all 0.3s ease-in-out",
        marginRight: theme.spacing(0.5),
    })
);

const SearchMenuItem = styled(MenuItem)(
    ({ theme }) => ({
        color: "inherit",
        fontFamily: "inter",
        fontSize: "1.5rem",
        fontWeight: 500,
        padding: theme.spacing(1, 2),
        textTransform: "capitalize",
    })
);

const steps = [
    { title: "Select a Location", description: `You can check an URL from one of the ${vantagePoints.length} locations.` },
    { title: "Enter an URL", description: "A backend server deployed on Google Cloud Run at this location will launch a chrome instance to visit the URL and use LocEdge to parse the captured HAR file." },
    { title: "Wait for the Result", description: "The result provides rich information about the CDN edge servers that delivered web resources, such as location, cache hit ratio, performance, etc." },
];

const DemoStepper = (props) => {
    const [step, setStep] = useState(0);

    const handleStep = (newVal) => () => {
        setStep(newVal);
    };

    return (
        <Box sx={{
            marginTop: 7.5,
            marginBottom: 4.5,
            width: { xs: "100%", sm: "88vw", md: "76vw", lg: "64vw", xl: "56vw" }
        }}>
            <Stepper nonLinear alternativeLabel activeStep={step} sx={{
                "& .Mui-active circle": {
                    color: "#9c27b0",
                    fill: "#9c27b0"
                },
            }}>
                {steps.map(({ title }, index) => (
                    <Step key={index}>
                        <StepButton
                            disableRipple
                            onClick={handleStep(index)}
                            sx={{ textAlign: "left" }}
                        >
                            <span style={{
                                fontFamily: "montserrat",
                                fontWeight: 500,
                                display: "inline-block",
                            }}>
                                {title}
                            </span>
                        </StepButton>
                    </Step>
                ))}
            </Stepper>
        </Box>
    );
};

export default function Demo(props) {
    const [loc, setLoc] = useState(0);
    const [focus, setFocus] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [msg, setMsg] = useState(null);
    const inputEl = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        const inputPrompt = new Typed(inputEl.current, {
            strings: promptStrings,
            typeSpeed: 30,
            backSpeed: 24,
            backDelay: 3200,
            smartBackspace: false,
            attr: "placeholder",
            loop: true,
            shuffle: true,
        });
        return () => inputPrompt.destroy();
    }, []);

    const handleSearch = (event) => {
        event.preventDefault();
        const url = inputEl.current.value;
        if (!url) return;
        if (!validator.isURL(url)) {
            setMsg("Invalid URL");
            return;
        }
        navigate({
            pathname: "/result",
            search: `?${createSearchParams({
                url, from: vantagePoints[loc].name,
            })}`,
        }, { state: { serverURL: vantagePoints[loc].url || "test" } });
    };

    return (
        <DemoBox>
            <Box sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
            }}>
                <Box sx={{
                    maxWidth: "max(40vw, 40rem)",
                    position: "relative",
                    overflow: "visible",
                    "::after": {
                        position: "absolute",
                        left: "50%",
                        top: "46%",
                        transform: "translate(-50%, -50%)",
                        content: `'${vantagePoints[loc].name}'`,
                        fontSize: `min(max(${100 / vantagePoints[loc].name.length}vw, 10vw), 16vw)`,
                        fontWeight: 800,
                        fontFamily: "montserrat",
                        textTransform: "uppercase",
                        whiteSpace: "nowrap",
                        zIndex: -1,
                        color: "#ddd",
                        overflow: "hidden",
                        transition: "all 0.25s",
                    },
                }}>
                    <SwipeableViews
                        axis="x"
                        index={loc}
                        onChangeIndex={(newVal) => setLoc(newVal)}
                    >
                        {vantagePoints.map(({ name }, index) => (
                            <Box
                                key={index}
                                component="img"
                                src={`${process.env.PUBLIC_URL}/landmarks/${name}.svg`}
                                alt={`${name}-landmark`}
                                sx={{
                                    width: "100%",
                                    maxHeight: "40vh",
                                }}
                            />
                        ))}
                    </SwipeableViews>
                    <MobileStepper
                        steps={vantagePoints.length}
                        position="static"
                        activeStep={loc}
                        nextButton={
                            <IconButton
                                onClick={() => setLoc((prev) => prev + 1)}
                                disabled={loc === vantagePoints.length - 1}
                                size="large"
                            >
                                <ArrowForwardIos />
                            </IconButton>
                        }
                        backButton={
                            <IconButton
                                onClick={() => setLoc((prev) => prev - 1)}
                                disabled={loc === 0}
                                size="large"
                            >
                                <ArrowBackIosNew />
                            </IconButton>
                        }
                        sx={{
                            "& .MuiMobileStepper-dotActive": {
                                backgroundColor: "#9c27b0",
                            },
                            backgroundColor: "transparent",
                        }}
                    />
                </Box>
                <SearchBox>
                    <SearchForm focus={focus} onSubmit={handleSearch}>
                        <SearchInput
                            startAdornment={<SearchIcon sx={{ marginRight: 1.5 }} />}
                            endAdornment={(
                                <SearchIconWrapper color="inherit" onClick={(event) => setAnchorEl(event.currentTarget)}>
                                    <SearchLocation id="icon-text">
                                        {vantagePoints[loc].country}
                                    </SearchLocation>
                                    <LocationOnOutlinedIcon />
                                </SearchIconWrapper>
                            )}
                            inputRef={inputEl}
                            onFocus={() => setFocus(true)}
                            onBlur={() => setFocus(false)}
                        />
                        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                            {vantagePoints.map(({ name, country }, idx) => (
                                <SearchMenuItem
                                    key={name}
                                    selected={idx === loc}
                                    onClick={() => {
                                        setLoc(idx);
                                        setAnchorEl(null);
                                    }}
                                >
                                    {`${country} ${name}`}
                                </SearchMenuItem>
                            ))}
                        </Menu>
                    </SearchForm>
                </SearchBox>
                <DemoStepper />
                <Snackbar
                    open={Boolean(msg)}
                    onClose={() => setMsg(null)}
                    message={msg}
                    autoHideDuration={3000}
                    action={(
                        <IconButton size="small" color="inherit" onClick={() => setMsg(null)}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    )}
                />
            </Box>
        </DemoBox>
    );
};